import React, { useRef, useState } from 'react';
import { App, Button, Input, Space, Tooltip, theme } from 'antd';
import { ApiOutlined, HistoryOutlined, SaveOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createShareLink, logRequestFailure, saveResult } from '../../shared/api/client';
import { buildPlatformShareLink } from '../../shared/lib/platformShareLink';
import { stickyToolbarStyle } from '../../shared/lib/stickyToolbar';
import { useVisibleBox } from '../../shared/lib/visibleBox';
import History from '../history/History';
import McpGuideModal from '../header/McpGuideModal';

interface UserHistoryBarProps {
  /**
   * Собирает текущий payload калькулятора в момент нажатия «Сохранить».
   * Функция, а не готовый объект: DashboardView не подписывает виджет на
   * каждое изменение калькулятора ради значения, нужного только по клику.
   */
  buildPayload: () => Record<string, unknown>;
  /** Загрузку выбранной записи в калькулятор делает DashboardView. */
  onLoad: (fileName: string) => void;
  /** Расчёт, полученный по share-id из панели истории, применяет DashboardView. */
  onLoadShared: (data: Record<string, unknown>) => void;
  /** Имя расчёта, загруженного из истории — подставляется в поле названия. */
  loadedFileName?: string;
}

/**
 * Панель «название + Сохранить + История» для монтирования без карточки
 * товара (`/apps/profitCalculator` без `productId`). Тот же набор действий,
 * что у `ProductHistory`, но история привязана к пользователю (`saved_results`
 * / `/api/user-results`), а не к товару — как в старом iframe-туле.
 */
const UserHistoryBar: React.FC<UserHistoryBarProps> = ({ buildPayload, onLoad, onLoadShared, loadedFileName }) => {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const { token } = theme.useToken();

  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [mcpOpen, setMcpOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  // Панель встаёт по видимой части блока модуля — см. useVisibleBox.
  const visibleBox = useVisibleBox(anchorRef, historyOpen);

  React.useEffect(() => {
    if (loadedFileName) setTitle(loadedFileName);
  }, [loadedFileName]);

  const handleSave = async () => {
    const name = title.trim();
    if (!name) {
      message.warning(t('idea_calc.user_history.title_required'));
      return;
    }
    setSaving(true);
    try {
      const res = await saveResult(name, buildPayload());
      if (res.success) {
        message.success(t('idea_calc.user_history.save_success'));
      } else {
        message.error(res.message || t('idea_calc.user_history.save_error'));
      }
    } catch (error: unknown) {
      logRequestFailure('User history save error:', error);
      message.error(t('idea_calc.user_history.save_error'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Поделиться = сохранить под введённым именем, получить share-id и отдать
   * ссылку на standalone-роут ПЛАТФОРМЫ (`/apps/profitCalculator?share=<id>`):
   * получатель заходит через платформу под своей учёткой, а не на публичную
   * страницу модуля. Origin — страницы хоста: на stage и prod он разный.
   */
  const handleShare = async () => {
    const name = title.trim();
    if (!name) {
      message.warning(t('idea_calc.user_history.title_required'));
      return;
    }
    setSharing(true);
    try {
      const saved = await saveResult(name, buildPayload());
      if (!saved.success) {
        message.error(saved.message || t('idea_calc.user_history.save_error'));
        return;
      }
      const { share_id: shareId } = await createShareLink(name);
      const link = buildPlatformShareLink(window.location.origin, shareId);
      try {
        await navigator.clipboard.writeText(link);
        message.success(t('idea_calc.user_history.share_copied'));
      } catch {
        // Буфер недоступен (нет разрешения, небезопасный контекст) — показываем
        // ссылку, чтобы пользователь скопировал её руками.
        modal.info({ title: t('idea_calc.user_history.share_link_label'), content: link });
      }
    } catch (error: unknown) {
      logRequestFailure('User history share error:', error);
      message.error(t('idea_calc.user_history.share_error'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
      <div ref={anchorRef} style={stickyToolbarStyle(token.colorBgLayout, token.paddingXS)}>
        <Space.Compact style={{ flex: '0 1 340px', minWidth: 200 }}>
          <Input
            placeholder={t('idea_calc.user_history.title_placeholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onPressEnter={handleSave}
            disabled={saving || sharing}
          />
          <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={sharing} onClick={handleSave}>
            {t('idea_calc.user_history.save_button')}
          </Button>
        </Space.Compact>

        <Button icon={<ShareAltOutlined />} loading={sharing} disabled={saving} onClick={handleShare}>
          {t('idea_calc.user_history.share_button')}
        </Button>

        <Button
          icon={<HistoryOutlined />}
          // Повторное нажатие закрывает: кнопка выглядит как переключатель
          // панели, и «открыть ещё раз» — единственное, чего от неё не ждут.
          onClick={() => setHistoryOpen((isOpen) => !isOpen)}
        >
          {t('idea_calc.user_history.open_button')}
        </Button>

        <Tooltip title={t('idea_calc.user_history.mcp_tooltip')}>
          <Button icon={<ApiOutlined />} onClick={() => setMcpOpen(true)}>
            {t('idea_calc.user_history.mcp_button')}
          </Button>
        </Tooltip>
      </div>

      <McpGuideModal open={mcpOpen} onClose={() => setMcpOpen(false)} />

      <History
        inline
        visibleBox={visibleBox}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onLoadResult={onLoad}
        onLoadShared={onLoadShared}
      />
    </>
  );
};

export default UserHistoryBar;
